import { useCallback, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { toast } from "sonner";
import { Moon, Sun } from "@/components/ui/icons";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { extractApiErrorMessage } from "@/lib/api";
import {
  forceSleepPair,
  setPairMode,
  sleepPair,
  wakePair,
} from "@/lib/api/account-pairs";
import { cn } from "@/lib/utils";

/**
 * Per-pair sleep/wake control. Used in:
 *   - the terminal accounts rail (one toggle, acts on the parent
 *     account_pair)
 *   - the pair detail headers (`/pairs/:slug` and `/pairs/:slug/:symbol`)
 *
 * Operator intent is at the account_pair level — sleeping the pair
 * closes both legs' Brave windows as a unit. Local optimistic state
 * keeps the toggle responsive; the live store (`pair:sleep-state`
 * events forwarded via the HedgePair socket) resolves the actual
 * state afterwards. Failed mutations revert and surface via toast.
 */

export interface PairSleepWakeState {
  mode: "auto" | "manual";
  manualState: "awake" | "asleep";
  /** Aggregated current state across both legs.
   *   awake     — both legs report awake
   *   asleep    — both legs report asleep
   *   partial   — legs disagree (mid-transition)
   *   unknown   — never acked
   *   orphan    — at least one leg's Brave didn't close on the last
   *               sleep attempt — operator should Retry Sleep or
   *               Force-kill the worker process
   */
  currentState: "awake" | "asleep" | "partial" | "unknown" | "orphan";
}

export function PairSleepWakeToggle({
  accountPairSlug,
  state,
  size = "default",
  className,
}: {
  /** account_pairs.slug — public id used by the four pair sleep
   *  endpoints. The terminal page (which only has a symbol-pair slug)
   *  must resolve to the parent slug before passing this prop. */
  accountPairSlug: string;
  state: PairSleepWakeState;
  size?: "sm" | "default";
  className?: string;
}) {
  const [busy, setBusy] = useState<"mode" | "sleep" | "wake" | "force" | null>(null);
  const [optimistic, setOptimistic] = useState<PairSleepWakeState | null>(null);

  const effective: PairSleepWakeState = optimistic ?? state;
  const isManual = effective.mode === "manual";
  const isOrphan = effective.currentState === "orphan";
  const isAsleep =
    !isOrphan &&
    (effective.currentState === "asleep" ||
      (effective.currentState === "unknown" &&
        isManual &&
        effective.manualState === "asleep"));

  const onModeChange = useCallback(
    async (mode: "auto" | "manual") => {
      if (busy != null || mode === effective.mode) return;
      setBusy("mode");
      setOptimistic({ ...effective, mode });
      try {
        await setPairMode(accountPairSlug, mode);
      } catch (e) {
        setOptimistic(null);
        toast.error(extractApiErrorMessage(e));
      } finally {
        setBusy(null);
      }
    },
    [accountPairSlug, busy, effective],
  );

  const onSleep = useCallback(async () => {
    if (busy != null || !isManual) return;
    setBusy("sleep");
    setOptimistic({ ...effective, manualState: "asleep", currentState: "asleep" });
    try {
      await sleepPair(accountPairSlug);
    } catch (e) {
      setOptimistic(null);
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }, [accountPairSlug, busy, effective, isManual]);

  const onWake = useCallback(async () => {
    if (busy != null || !isManual) return;
    setBusy("wake");
    setOptimistic({ ...effective, manualState: "awake", currentState: "awake" });
    try {
      await wakePair(accountPairSlug);
    } catch (e) {
      setOptimistic(null);
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }, [accountPairSlug, busy, effective, isManual]);

  const onForceKill = useCallback(async () => {
    if (busy != null || !isManual) return;
    setBusy("force");
    try {
      const res = await forceSleepPair(accountPairSlug);
      if (res.dispatched === 0) {
        toast.message("No orphan legs to force-kill — state may have already recovered.");
      } else {
        toast.success(`Force-kill dispatched to ${res.dispatched} leg${res.dispatched === 1 ? "" : "s"}.`);
      }
    } catch (e) {
      toast.error(extractApiErrorMessage(e));
    } finally {
      setBusy(null);
    }
  }, [accountPairSlug, busy, isManual]);

  // Reset the optimistic snapshot when the prop catches up — the
  // server has confirmed and our local guess is no longer needed.
  if (
    optimistic &&
    optimistic.mode === state.mode &&
    optimistic.manualState === state.manualState &&
    optimistic.currentState === state.currentState
  ) {
    queueMicrotask(() => setOptimistic(null));
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1.5",
        size === "sm" ? "text-[10px]" : "text-xs",
        className,
      )}
      data-busy={busy != null}
    >
      <Select
        value={effective.mode}
        onValueChange={(v) => void onModeChange(v as "auto" | "manual")}
        disabled={busy != null}
      >
        <SelectTrigger
          size={size === "sm" ? "sm" : "default"}
          aria-label="Pair bot mode"
          className={cn(
            "gap-2",
            size === "sm" ? "text-[11px]" : "text-xs",
            "border-input hover:border-accent/40",
            busy != null && "opacity-60",
          )}
        >
          <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
            Bot Mode
          </span>
          <SelectValue>
            <span className="font-medium text-foreground capitalize">
              {effective.mode}
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent align="end" alignItemWithTrigger={false} className="min-w-[min(18rem,calc(100vw-1.5rem))]">
          <SelectItem value="auto">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">Auto</span>
              <span className="text-[10px] text-muted-foreground">
                Manager opens / closes Brave on its own schedule.
              </span>
            </div>
          </SelectItem>
          <SelectItem value="manual">
            <div className="flex flex-col gap-0.5">
              <span className="text-xs font-medium text-foreground">Manual</span>
              <span className="text-[10px] text-muted-foreground">
                Operator drives Brave. Sleep / Wake stays in your hands.
              </span>
            </div>
          </SelectItem>
        </SelectContent>
      </Select>

      <AnimatePresence mode="wait" initial={false}>
        {isManual && (
          <motion.button
            key={isOrphan ? "retry" : isAsleep ? "wake" : "sleep"}
            initial={{ opacity: 0, scale: 0.85 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (isOrphan) void onSleep();
              else if (isAsleep) void onWake();
              else void onSleep();
            }}
            disabled={busy != null}
            className={cn(
              "inline-flex items-center gap-1 rounded-md border bg-card/50 px-1.5 font-medium uppercase tracking-wide transition-colors hover:bg-accent/10",
              size === "sm" ? "h-5 text-[9px]" : "h-6 text-[10px]",
              isOrphan
                ? "border-destructive/60 text-destructive ring-2 ring-destructive/30 hover:border-destructive"
                : "border-border/60 hover:border-accent/40",
              busy != null && "opacity-60",
            )}
            title={
              isOrphan
                ? "A leg’s Brave didn’t close on the last sleep — click to retry."
                : isAsleep
                  ? "Wake pair"
                  : "Sleep pair"
            }
          >
            {isOrphan ? (
              <>
                <Moon aria-hidden="true" className="size-3" />
                Retry Sleep
              </>
            ) : isAsleep ? (
              <>
                <Sun aria-hidden="true" className="size-3" />
                Wake
              </>
            ) : (
              <>
                <Moon aria-hidden="true" className="size-3" />
                Sleep
              </>
            )}
          </motion.button>
        )}
      </AnimatePresence>

      {isManual && isOrphan && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            void onForceKill();
          }}
          disabled={busy != null}
          className={cn(
            "inline-flex items-center rounded-md border border-destructive/40 bg-destructive/10 px-1.5 font-medium uppercase tracking-wide text-destructive transition-colors hover:border-destructive hover:bg-destructive/20",
            size === "sm" ? "h-5 text-[9px]" : "h-6 text-[10px]",
            busy != null && "opacity-60",
          )}
          title="Skip drain. Send SIGKILL to the orphaned worker process. Use only if Retry Sleep keeps failing."
        >
          Force Kill
        </button>
      )}

      {/* Static (non-pulsing) state indicator. */}
      <span
        className={cn(
          "inline-flex h-4 w-4 items-center justify-center rounded-full",
          isOrphan
            ? "text-destructive"
            : isAsleep
              ? "text-muted-foreground"
              : effective.currentState === "partial"
                ? "text-amber-500"
                : "text-emerald-500",
        )}
        title={
          effective.currentState === "orphan"
            ? "A leg’s Brave is still running — click Retry Sleep, or Force Kill if it persists."
            : effective.currentState === "unknown"
              ? "Pair worker state unknown"
              : effective.currentState === "asleep"
                ? "Both legs asleep"
                : effective.currentState === "partial"
                  ? "Legs disagree (transitioning)"
                  : "Both legs awake"
        }
      >
        {isAsleep ? <Moon aria-hidden="true" className="size-3" /> : <Sun aria-hidden="true" className="size-3" />}
      </span>
    </div>
  );
}
